import { ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import Alert, { AlertVariant } from "@galaxy-io/dls/feedback/Alert";
import { InputVariant } from "@galaxy-io/dls/inputs/Input";
import PasswordInput from "@galaxy-io/dls/inputs/PasswordInput";
import TagInput, { TagInputVariant } from "@galaxy-io/dls/inputs/TagInput";
import TextInput from "@galaxy-io/dls/inputs/TextInput";
import DescriptionList from "@galaxy-io/dls/lists/DescriptionList";
import Text, { TextSize } from "@galaxy-io/dls/text/Text";
import { ToastVariant } from "@galaxy-io/dls/toast/Toast";
import { useToast } from "@galaxy-io/dls/toast/useToast";
import type { Integration } from "@gtm/contracts";
import type { PosthogConfig } from "@gtm/contracts/integrations/posthog";
import { GlobeIcon } from "@phosphor-icons/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useRouter } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import { Button } from "@/components/ui/button";
import { DrawerList, DrawerRow, DrawerSection } from "@/components/workspace/drawer-content";
import { Inline, Stack } from "@/components/workspace/people-styles";
import { SelectField } from "@/components/workspace/select-field";
import { SourceIcon } from "@/components/workspace/source-icon";
import { apiClient, unwrap } from "@/lib/api-client";
import { toastDefaults } from "@/lib/toast";
import { ConnectionSurface } from "./connection-surface";
import { IntegrationDrawerContent } from "./integration-drawer-content";
import { useConnectionReset } from "./use-connection-reset";

export function PosthogConnectionForm({
  open,
  selected,
  config,
  onClose,
  onSaved,
}: {
  open: boolean;
  selected?: Integration;
  config?: PosthogConfig;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const [formError, setFormError] = useState<string | undefined>();
  const queryClient = useQueryClient();
  const router = useRouter(),
    { toast } = useToast();
  const [name, setName] = useState(selected?.name || "PostHog"),
    [apiKey, setApiKey] = useState(""),
    [busy, setBusy] = useState(false);
  const [region, setRegion] = useState(config?.region || "us");
  const [projectId, setProjectId] = useState(config?.projectId || "");
  const [domains, setDomains] = useState<string[]>(config?.domains ?? []);
  function notify(header: string, subheader: string) {
    toast({
      variant: ToastVariant.SUCCESS,
      header,
      description: subheader,
      ...toastDefaults(ToastVariant.SUCCESS),
    });
  }
  useConnectionReset(open, !!selected, () => {
    setFormError(undefined);
    setName(selected?.name || "PostHog");
    setApiKey("");
    setRegion(config?.region || "us");
    setProjectId(config?.projectId || "");
    setDomains(config?.domains ?? []);
    setBusy(false);
  });
  async function action<T>(request: () => Promise<T>) {
    setFormError(undefined);
    setBusy(true);
    try {
      const data = await request();
      await queryClient.invalidateQueries({ queryKey: ["integrations"] });
      router.refresh();
      return data;
    } catch (e) {
      setFormError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const testButton = selected?.enabled && (
    <Inline>
      <Button
        label="Test connection"
        variant={ButtonVariant.SECONDARY}
        isDisabled={busy}
        onClick={async () => {
          const result = await action(() =>
            unwrap(
              apiClient().POST("/integrations/posthog/connections/{id}/test", {
                params: { path: { id: selected.id } },
              }),
            ),
          );
          if (result?.ok)
            notify(
              "Connection verified",
              "PostHog accepted the key and allowed a query against this project.",
            );
        }}
      />
    </Inline>
  );
  return (
    <ConnectionSurface
      open={open}
      selected={selected}
      flush
      title={selected?.name || "Connect PostHog"}
      headerLeading={<SourceIcon source="posthog" size={36} />}
      details={
        selected && (
          <Stack>
            <DrawerList>
              <DescriptionList
                items={[
                  {
                    label: "Region",
                    value: config?.region === "eu" ? "EU Cloud" : "US Cloud",
                  },
                  { label: "Project ID", value: config?.projectId },
                ]}
              />
            </DrawerList>
            <DrawerSection header="Domains" icon={GlobeIcon} defaultIsOpen>
              <DrawerList>
                {config?.domains.map((domain) => (
                  <DrawerRow key={domain} label={domain}>
                    Tracked
                  </DrawerRow>
                ))}
              </DrawerList>
            </DrawerSection>
            {testButton}
            <Link href={`/observability?view=website&connection=${selected.id}`}>
              View website metrics
            </Link>
          </Stack>
        )
      }
      onClose={onClose}
      busy={busy}
      dirty={
        !!apiKey ||
        name !== (selected?.name || "PostHog") ||
        region !== (config?.region || "us") ||
        projectId !== (config?.projectId || "") ||
        domains.join(",") !== (config?.domains.join(",") || "")
      }
      footer={
        <Button
          label={selected ? "Save connection" : "Connect PostHog"}
          isLoading={busy}
          isDisabled={
            busy || !name.trim() || !projectId.trim() || !domains.length || (!selected && !apiKey)
          }
          onClick={async () => {
            const body = {
              name,
              region,
              projectId,
              domains,
              ...(apiKey ? { apiKey } : {}),
            };
            const result = await action(() =>
              selected
                ? unwrap(
                    apiClient().PUT("/integrations/posthog/connections/{id}", {
                      params: { path: { id: selected.id } },
                      body,
                    }),
                  )
                : unwrap(
                    apiClient().POST("/integrations/posthog/connections", {
                      body,
                    }),
                  ),
            );
            if (result) {
              setApiKey("");
              await queryClient.invalidateQueries({ queryKey: ["posthog-config"] });
              await queryClient.invalidateQueries({ queryKey: ["website-overview"] });
              await queryClient.invalidateQueries({ queryKey: ["website-sessions"] });
              notify("PostHog configured", "Website metrics are available in Observability.");
              onSaved(result.id);
            }
          }}
        />
      }
    >
      {formError && <Alert variant={AlertVariant.ERROR}>{formError}</Alert>}
      <IntegrationDrawerContent>
        <Stack>
          <TextInput
            ref={(input) => input?.setAttribute("aria-label", "Integration name")}
            label="Integration name"
            value={name}
            onChange={setName}
            variant={InputVariant.BASE}
            fillWidth
            isDisabled={busy}
          />
          <SelectField
            label="PostHog region"
            value={region}
            onChange={(value) => setRegion(value as "us" | "eu")}
            disabled={busy}
            options={[
              { value: "us", label: "US Cloud" },
              { value: "eu", label: "EU Cloud" },
            ]}
          />
          <TextInput
            label="Project ID"
            ref={(input) => input?.setAttribute("aria-label", "Project ID")}
            value={projectId}
            onChange={setProjectId}
            placeholder="12345"
            fillWidth
            variant={InputVariant.BASE}
            isDisabled={busy}
          />
          <TagInput
            label="Domains"
            ref={(input) => input?.setAttribute("aria-label", "Domains")}
            value={domains}
            onChange={setDomains}
            placeholder="Add a domain, e.g. www.example.com"
            fillWidth
            variant={TagInputVariant.BASE}
            isDisabled={busy}
          />
          <Text size={TextSize.BODY_SM}>
            Press Enter or comma to add a domain. Select one or more domains in Observability.
          </Text>
          <PasswordInput
            ref={(input) => input?.setAttribute("aria-label", "Personal API key")}
            label="Personal API key"
            placeholder={selected ? "Leave blank to keep the saved key" : "phx_…"}
            value={apiKey}
            onChange={setApiKey}
            fillWidth
            variant={InputVariant.BASE}
            isDisabled={busy}
          />
          <Text size={TextSize.BODY_SM}>
            Create a personal API key in PostHog account settings with Query Read and Session
            Recording Read permissions and access to this project. The key is stored encrypted and
            never displayed again.
          </Text>

          {testButton}
        </Stack>
      </IntegrationDrawerContent>
    </ConnectionSurface>
  );
}

export function PosthogConnection(props: {
  open: boolean;
  selected?: Integration;
  onClose: () => void;
  onSaved: (id: string) => void;
}) {
  const id = props.selected?.id;
  const settings = useQuery({
    queryKey: ["posthog-config", id],
    enabled: props.open && !!id,
    queryFn: () =>
      unwrap(
        apiClient().GET("/integrations/posthog/connections/{id}", {
          params: { path: { id: id! } },
        }),
      ),
  });
  if (id && !settings.data)
    return (
      <ConnectionSurface
        open={props.open}
        selected={props.selected}
        title="PostHog"
        onClose={props.onClose}
        details={
          <QueryState
            error={settings.error}
            loadingMessage="Loading PostHog settings…"
            onRetry={() => void settings.refetch()}
          />
        }
      >
        <QueryState
          error={settings.error}
          loadingMessage="Loading PostHog settings…"
          onRetry={() => void settings.refetch()}
        />
      </ConnectionSurface>
    );
  return <PosthogConnectionForm {...props} config={settings.data} />;
}
